package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.copy
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rest.unifiedaggregator.asEntities
import com.siriusxm.pia.rest.unifiedaggregator.getIncomingEntityUi
import com.siriusxm.pia.views.unifiedaggregator.smithy.ENTITY_ID_SHAPE_ID
import com.siriusxm.pia.views.unifiedaggregator.smithy.SmithyStructEditor
import com.siriusxm.pia.views.unifiedaggregator.smithy.SmithyViewOptions
import com.siriusxm.pia.views.unifiedaggregator.smithy.SmithyViewerStyles
import com.siriusxm.pia.views.unifiedaggregator.smithy.aggregatorRenderers
import com.siriusxm.smithy4kt.*
import com.siriusxm.unifiedcontent.console.aggregator.intakeErrorMessage
import com.siriusxm.unifiedcontent.console.aggregator.rememberPublishablePartials
import unifiedcontent.aggregator.*
import contentingestion.unifiedmodel.entityId
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.withTimeout
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Clock
import kotlin.time.Duration.Companion.seconds
import kotlin.time.Instant

/**
 * An editor to submit partial updates on behalf of the console.
 *
 * The console does not choose a producer. The aggregator resolves the producer from the access
 * token's Cognito client id, which is the console's own producer (UCA-1152), and authorizes the
 * user through AVP (`UnifiedContentAggregator_PartialUpdate`, UCA-1107). So the partials offered here
 * are the ones the console producer is configured for on this entity's type (a partial outside that
 * set would be accepted and then silently dropped downstream) that AVP also allows this user to publish
 * for this entity. Both checks only hide options; the aggregator enforces them on submit.
 *
 * @param app the aggregator service
 * @param entityId the typed id of the entity to update; becomes the modification's `id`
 * @param partialId the partial to edit, or null to let the user pick one
 */
@Composable
fun partialEditor(
    app: AggregatorService,
    entityId: entityId,
    partialId: PartialId? = null
) {
    var base by remember { mutableStateOf<com.siriusxm.pia.rest.unifiedaggregator.Entity?>(null) }

    // Null until the entity has loaded and AVP has answered for every candidate partial.
    val availablePartials: List<PartialSchema>? by app.rememberPublishablePartials(entityId, base)

    var selectedPartial by remember { mutableStateOf<PartialSchema?>(null) }
    var schedule by remember { mutableStateOf<Instant?>(null) }

    // the modification that we'll be submitting.
    var modification by remember { mutableStateOf(buildJsonObject { }) }
    var isModificationValid by remember { mutableStateOf(false) }
    var validateInput by remember { mutableStateOf(false) }

    suspend fun submit() {
        if (!isModificationValid) {
            validateInput = true
            app.context.showError(
                "There are validation issues with your changes. Please review the highlighted fields."
            )
            return
        }
        val selectedPartialId = selectedPartial?.id
        val path = selectedPartial?.paths?.firstOrNull()
        if (selectedPartialId == null || path == null) {
            app.context.showError("No partial selected.")
            return
        }
        if (modification.isEmpty()) {
            app.context.showError("No changes to submit.")
            return
        }
        try {
            val ts = Clock.System.now()
            val cleaned = modification.stripArrayNulls().jsonObject
            val update = PartialUpdate(
                ContentUpdateType.ADD,
                ts,
                cleaned.copy {
                    put("id", entityId)
                    put("type", base?.type)
                    put("version", base?.version)
                },
                selectedPartialId,
                operations = listOf(
                    PartialUpdateOperation(
                        PartialUpdateOperationType.REPLACE,
                        path, path
                    )
                ),
                publishTs = schedule,
                auditContext = AuditContext(
                    app.context.viewer.email
                )
            )
            app.clientApi.postPartialContentUpdate(update)

            // now we'll wait until we see this update
            try {
                withTimeout(20.seconds) {
                    var found = false
                    while (!found) {
                        delay(1.seconds)
                        found = app.clientApi.getIncomingEntityUi(
                            entityId,
                            base?.type!!,
                            base?.version!!
                        ).partials?.any {
                            it.ts == ts
                        } ?: false
                    }
                }
            } catch (e: TimeoutCancellationException) {
                app.context.showError(
                    "The partial was published but hasn't been saved yet. It might take more time to propagate.",
                    e.message
                )

            }
            app.navigate("entity/${encodeURIComponent(entityId)}/incoming")
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            val error = intakeErrorMessage("publish this partial", e)
            app.context.showError(error.message, error.description)
        }
    }

    /**
     * Load the base data of the entity
     */
    LaunchedEffect(entityId) {
        val loadedEntities = app.clientApi.getEntityById(entityId).asEntities()
        base = loadedEntities.maxByOrNull { it.version }
    }

    /**
     * If a partial is provided, select it.
     */
    LaunchedEffect(partialId, availablePartials) {
        availablePartials?.find {
            it.id == partialId
        }?.let {
            selectedPartial = it
        }
    }

    waitUntilNonNull(availablePartials) { partials ->
        dialogView(base?.name ?: "") {
            listOfNotNull("Update")

            breadcrumbs {
                crumb("Aggregator", "aggregator")
                crumb("Entities", "aggregator/entity")
                base?.name?.let {
                    crumb(it, "aggregator/entity/${encodeURIComponent(entityId)}")
                }
            }

            action {
                title = "Cancel"
                action {
                    app.navigate("entity/${encodeURIComponent(entityId)}")
                }
            }

            action {
                title = "Reset"
                action {
                    modification = buildJsonObject { }
                    schedule = null
                }
            }
            action {
                title = "Submit"
                enabled = modification.isNotEmpty()
                primary = true
                showProgressOnAction = true
                action {
                    submit()
                }
            }

            content {
                if (partials.isNotEmpty()) {
                    // only allow selection of a partial if it wasn't provided
                    if (partialId == null) {
                        dialogField("Partial") {
                            if (partials.size == 1) {
                                Text(partials.first().let { it.name ?: it.id })
                            } else {
                                simpleSelectNullable(selectedPartial, partials.map {
                                    (it.name ?: it.id) to it
                                }) {
                                    selectedPartial = it
                                }
                            }
                        }
                    }


                    val partial = selectedPartial
                    if (partial != null) {
                        dialogField(
                            "Schedule (Your local time)",
                            instruction = "Leave blank to publish immediately"
                        ) {
                            instantPicker(schedule) {
                                schedule = it
                            }
                        }

                        val allowedPaths = partial.paths
                        val shapeId = base?.type?.let {
                            app.entityType(it)
                        }?.shapeId

                        val entityShape = if (shapeId != null) {
                            app.entityModel?.getShape(shapeId)
                        } else null

                        if (entityShape != null) {
                            dialogField("Updates") {
                                allowedPaths.forEach { jsonPath ->
                                    partialShapeEditor(
                                        app,
                                        base!!.data,
                                        modification,
                                        jsonPath,
                                        entityShape,
                                        validateInput
                                    ) { mod, isValid ->
                                        modification = mod
                                        isModificationValid = isValid
                                    }
                                }
                            }
                        }
                    }

                } else {
                    messageBox("You do not have permission to publish a partial update for this entity.")
                }
            }
        }
    }
}

/**
 * The root of the partial editor control. Delegates most work to the SmithyView hierarchy.
 */
@Composable
fun partialShapeEditor(
    aggregator: AggregatorService,
    entity: JsonElement, modification: JsonObject, jsonPath: String,
    rootShape: SmithyShape,
    validate: Boolean = false,
    onUpdate: (JsonObject, Boolean) -> Unit
) {
    Style(SmithyViewerStyles)


    box({
        paddedContent = false
    }) {
        val rootOfEdits = rootShape.resolveJsonPath(jsonPath)
        if (rootOfEdits is SmithyMember) {
            val parent = rootOfEdits.parent
            if (parent is SmithyStructure) {
                val baseData = entity.querySingle(jsonPath)
                val editor = SmithyStructEditor(parent).apply {
                    includeFields(listOf(rootOfEdits.name))

                    this.onUpdate = { updatedValue ->
                        val editValue = updatedValue?.jsonObject?.get(rootOfEdits.name)
                        val isValid = try {
                            editValue?.let {
                                // Relax validation only at fields whose schema type is
                                // entityId: the schema's entityId pattern expects bare GUIDs,
                                // but the aggregator accepts source-formatted ids
                                // (e.g. "TL:abc-...") and maps them server-side. Other fields
                                // continue to be strictly validated.
                                rootOfEdits.validate(
                                    it.relaxTypedEntityIdsForValidation(rootOfEdits),
                                    true
                                )
                                true
                            } ?: false
                        } catch (_: Throwable) {
                            false
                        }

                        onUpdate(updatedValue as? JsonObject ?: buildJsonObject { }, isValid)
                    }
                }
                editor.edit(buildJsonObject {
                    if (baseData != null) {
                        put(rootOfEdits.name, baseData)
                    }
                }, modification, SmithyViewOptions().apply {
                    showValidationIssues = validate
                    aggregatorRenderers(aggregator)
                })
            }
        }
    }
}

/**
 * Recursively strips JsonNull elements from any JsonArray in the tree. Object entries
 * keep JsonNull values (an explicit null on a field is meaningful in REPLACE partials),
 * but list slots with no chosen value are placeholders rendered by the editor and must
 * not be persisted as part of the submitted array.
 *
 * Assumption: no list shape currently in the unified model treats null as a meaningful
 * element. If a future array field needs null elements, this scrub will silently drop
 * them — at that point this should be scoped to the typed-id list paths (or made
 * schema-aware) rather than walking the whole submission tree.
 */
internal fun JsonElement.stripArrayNulls(): JsonElement = when (this) {
    is JsonArray -> JsonArray(
        this.filter { it !is JsonNull }.map { it.stripArrayNulls() }
    )
    is JsonObject -> JsonObject(this.mapValues { (_, v) -> v.stripArrayNulls() })
    else -> this
}

private val typedEntityIdPattern = Regex("^[A-Za-z]{1,8}:[\\w\\-]+$")
private const val placeholderGuidForValidation = "00000000-0000-0000-0000-000000000000"

/**
 * Walks the JSON tree alongside its Smithy shape and rewrites only the values that
 * sit at an entityId-typed position to a placeholder GUID, when their string form
 * matches a typed-id ("PREFIX:identifier"). The aggregator accepts source-formatted
 * ids server-side, but the Smithy schema's entityId pattern allows only bare GUIDs
 * — so without this, valid submissions fail strict client-side validation. Only the
 * validation pass uses the rewritten tree; the original is what is submitted.
 */
internal fun JsonElement.relaxTypedEntityIdsForValidation(shape: SmithyEntity): JsonElement {
    return when (shape) {
        is SmithyMember -> relaxTypedEntityIdsForValidation(shape.shape)
        is SmithyStructure -> {
            val obj = this as? JsonObject ?: return this
            JsonObject(obj.mapValues { (key, value) ->
                val memberShape = shape.allMembers[key]
                if (memberShape != null) value.relaxTypedEntityIdsForValidation(memberShape)
                else value
            })
        }
        is SmithyList -> {
            val arr = this as? JsonArray ?: return this
            JsonArray(arr.map { it.relaxTypedEntityIdsForValidation(shape.member) })
        }
        is SmithyMap -> {
            val obj = this as? JsonObject ?: return this
            JsonObject(obj.mapValues { (_, value) ->
                value.relaxTypedEntityIdsForValidation(shape.value)
            })
        }
        is SmithyUnion -> {
            val obj = this as? JsonObject ?: return this
            JsonObject(obj.mapValues { (key, value) ->
                val variantShape = shape.members[key]
                if (variantShape != null) value.relaxTypedEntityIdsForValidation(variantShape)
                else value
            })
        }
        is SmithyShape -> {
            if (shape.id == ENTITY_ID_SHAPE_ID && this is JsonPrimitive && isString) {
                val str = contentOrNull
                if (str != null && typedEntityIdPattern.matches(str)) {
                    JsonPrimitive(placeholderGuidForValidation)
                } else this
            } else this
        }
        else -> this
    }
}
